import { apiError } from '../apiError';
import api from '../axios.config';
import { ApiResponse } from 'types';

/**
 * Per-organization feature ALLOW-list — the inverse of feature locks. A feature
 * here is OFF for every organization except those the platform grants it to.
 *
 * Mirrors GrantedFeature.java. The platform root (Green Giraffe) holds every
 * feature implicitly; a grant covers only the organization it names.
 *
 * Granting and revoking are MASTER_ADMIN/PLATFORM_ADMIN only on the server;
 * `mine` is open to everyone and reports the CURRENT organization's grants.
 */
export type GrantedFeature =
  | 'WHATSAPP_EMBEDDED_SIGNUP_CONFIG'
  | 'ADD_PROVIDER'
  | 'CAPACITY_MANAGE'
  | 'WORKER_CONFIG'
  | 'LIVE_CALL_SPY';

export const GRANTED_FEATURES: { key: GrantedFeature; label: string; help: string; businessGrantable?: boolean }[] = [
  {
    key: 'WHATSAPP_EMBEDDED_SIGNUP_CONFIG',
    label: 'WhatsApp Embedded Signup config',
    help:
      'Lets the provider’s own admins create and edit their OWN Meta app config. Their businesses then onboard ' +
      'through it instead of the platform’s. Not needed for signup to work — every org inherits the platform config.',
  },
  {
    key: 'ADD_PROVIDER',
    label: 'Manage own trunks (add provider)',
    help:
      'Lets the provider’s admins add, edit and delete their OWN external carrier trunks, add or remove numbers on ' +
      'them and run a health check. Without it they only see their own trunks, read-only. Green Giraffe’s platform ' +
      'trunks stay yours either way.',
  },
  {
    key: 'CAPACITY_MANAGE',
    label: 'Manage capacity limits',
    help:
      'Lets the provider’s admins create, change and delete CPS / channel limits for their own businesses, numbers ' +
      'and campaigns, within the ceiling you give them. Without it they see their limits and ceiling, read-only. ' +
      'Trunk ceilings stay yours either way.',
  },
  {
    key: 'WORKER_CONFIG',
    label: 'Worker config',
    help:
      'Shows the provider’s admins the Worker Config page and lets them change worker runtime settings ' +
      '(webhook consumer, CDR worker). These settings are platform-wide — they affect every organization — so ' +
      'without the grant the page is hidden entirely, including reading it.',
  },
  {
    key: 'LIVE_CALL_SPY',
    label: 'Live call listening',
    help:
      'Lets the organization’s admins Listen, Whisper or Barge into live calls of their own organization and its ' +
      'businesses (Live Calls). Privacy-sensitive, so off by default. Viewing and hanging up their own calls need no ' +
      'grant. Unlike the others it can be granted to a business as well as a provider; a provider’s grant also ' +
      'covers its admins while they act as one of its businesses, but not that business’s own admins.',
    businessGrantable: true,
  },
];

export interface FeatureGrant {
  id: string;
  organizationId: string;
  feature: GrantedFeature;
  grantedBy?: string;
  createdAt?: string;
  note?: string;
}

export interface MyGrants {
  organizationId: string;
  grants: GrantedFeature[];
}

const BASE = '/v1/feature-grants';

export const featureGrantsService = {
  mine: async (): Promise<ApiResponse<MyGrants>> => {
    try { return (await api.get(`${BASE}/mine`)).data; }
    catch (e: any) { return apiError(e, 'Failed to load feature grants'); }
  },

  list: async (feature?: GrantedFeature, organizationId?: string): Promise<ApiResponse<FeatureGrant[]>> => {
    try {
      const params: Record<string, string> = {};
      if (feature) params.feature = feature;
      if (organizationId) params.organizationId = organizationId;
      return (await api.get(BASE, { params })).data;
    } catch (e: any) { return apiError(e, 'Failed to load feature grants'); }
  },

  grant: async (organizationId: string, feature: GrantedFeature, note?: string): Promise<ApiResponse<FeatureGrant>> => {
    try { return (await api.post(BASE, { organizationId, feature, note })).data; }
    catch (e: any) { return apiError(e, 'Failed to grant the feature'); }
  },

  revoke: async (organizationId: string, feature: GrantedFeature): Promise<ApiResponse<{ revoked: boolean }>> => {
    try { return (await api.delete(BASE, { params: { organizationId, feature } })).data; }
    catch (e: any) { return apiError(e, 'Failed to revoke the feature'); }
  },
};
